import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { canUseDOM } from 'exenv';
import { FormattedMessage } from 'react-intl';
import VisibilitySensor from 'react-visibility-sensor';

const BRAINTREE_FIELDS = ['number', 'expirationMonth', 'expirationYear', 'cvv', 'postalCode'];

import { APPLE_PAY_IMPRESSION, APPLE_PAY_CLICK } from 'lib/analytics/events';
import { countryByLocale } from 'lib/locale';
import { dataCollectorShape, applePayInstanceShape } from 'lib/common/prop-types';

import { Location } from 'models/locations';

const propTypes = {
  submitCheckout: PropTypes.func.isRequired,

  id: PropTypes.string,
  textClassName: PropTypes.string,
  orLabel: PropTypes.string,
  className: PropTypes.string,
  display: PropTypes.bool,
  checkoutTotal: PropTypes.number,
  applePayInstance: applePayInstanceShape,
  dataCollector: dataCollectorShape,
  canSubmit: PropTypes.bool.isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  setUserInfo: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  position: PropTypes.string.isRequired,
  location: PropTypes.instanceOf(Location).isRequired,
  locale: PropTypes.string.isRequired,
};

const defaultProps = {
  buttonClassName: '',
  textClassName: '',
  id: '',
  orLabel: null,
  className: null,
  display: false,
  checkoutTotal: null,
  applePayInstance: null,
  dataCollector: null,
};

class ApplePayButton extends Component {
  constructor(props) {
    super(props);

    this.onClick = this.onClick.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.trackEvent = this.trackEvent.bind(this);

    this.state = {
      display: false,
    };
  }

  componentWillMount() {
    this.setState({
      display: canUseDOM && window.ApplePaySession,
    });
  }

  trackEvent(event) {
    const { trackEvent, position } = this.props;
    trackEvent({
      ...event,
      position,
    });
  }

  onClick() {
    const { checkoutTotal: amount, applePayInstance, locale, location } = this.props;
    const { ApplePaySession } = window;
    this.trackEvent(APPLE_PAY_CLICK);

    if (!applePayInstance) { return; }

    const countryCode = countryByLocale(locale);
    const { currency: currencyCode } = location;
    const label = 'ParkWhiz';

    const paymentRequest = applePayInstance.createPaymentRequest({
      total: { label, amount },
      requiredShippingContactFields: ['email', 'name'],
      countryCode,
      currencyCode,
    });

    const session = new ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = (event) => {
      applePayInstance.performValidation({
        validationURL: event.validationURL,
        displayName: 'ParkWhiz',
      }, (validationErr, merchantSession) => {
        if (validationErr) {
          // You should show an error to the user, e.g. 'Apple Pay failed to load.'
          console.error('Error validating merchant:', validationErr);
          session.abort();
          return;
        }
        session.completeMerchantValidation(merchantSession);
      });
    };

    session.onpaymentauthorized = (event) => {
      applePayInstance.tokenize({
        token: event.payment.token,
      }, (tokenizeErr, payload) => {
        if (tokenizeErr) {
          console.error('Error tokenizing Apple Pay:', tokenizeErr);
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          return;
        }
        session.completePayment(ApplePaySession.STATUS_SUCCESS);
        const { deviceData } = this.props.dataCollector || {};

        // Remove other braintree fields from form validation
        this.props.modifyRequiredFieldList({ remove: BRAINTREE_FIELDS });

        const {
          givenName: firstname,
          familyName: lastname,
          emailAddress: email,
        } = event.payment.shippingContact;
        let { phoneNumber: phone } = event.payment.shippingContact;
        if (phone) { phone = phone.replace(/\D/g, ''); }
        this.props.setUserInfo({ firstname, lastname, email, phone });
        this.props.submitCheckout({ paymentMethodNonce: payload.nonce, deviceData });
      });
    };

    session.begin();
  }

  handleVisibilityChange(visible) {
    if (visible) {
      this.trackEvent(APPLE_PAY_IMPRESSION);
    }
  }

  get display() {
    return this.state.display && this.props.display;
  }

  render() {
    if (!this.props.applePayInstance || !this.display) { return null; }

    const { id, textClassName, orLabel, className, canSubmit } = this.props;
    const or = (<div className="text-size-14 text-size-md-18 text-color-dark-slate margin-top-20 margin-bottom-20 text-weight-medium with-mid-line">
      <span className="background-color-white padding-left-20 padding-right-20 padding-left-md-40 padding-right-md-40">
        OR
      </span>
    </div>);

    const onClick = canSubmit ? this.onClick : () => {};

    const buttonClasses = cx({
      'btn': true,
      'btn-lg': true,
      'btn-block': true,
      'btn-book': true,
      'btn-primary': true,
      'progress-btn-background-black': true,
      'progress-btn-outer': true,
      'apple-pay-btn': true,
      'clickable': canSubmit,
      'btn-disabled': !canSubmit,
    });

    return (
      <div className={className}>
        {orLabel === 'before' ? or : null}
        <div
          className={buttonClasses}
          id={id}
          onClick={onClick}
          onTouchEnd={onClick}
        >
          <VisibilitySensor onChange={this.handleVisibilityChange} partialVisibility>
            <span className="progress-btn-wrap">
              <div className={`${textClassName} text text-weight-light`}>
                <div>
                  <FormattedMessage
                    id="checkout.buy-with"
                    defaultMessage="Buy with"
                  />
                  <span className="iconified iconified-apple-pay" />
                </div>
              </div>
              <span className="progress-btn">
                <span className="progress-btn-inner" />
              </span>
            </span>
          </VisibilitySensor>
        </div>
        {orLabel === 'after' ? or : null}
      </div>
    );
  }
}

ApplePayButton.propTypes = propTypes;
ApplePayButton.defaultProps = defaultProps;
export default ApplePayButton;
